// RequestsView.tsx - Updated to use only ConciseRequestDetailsView
import React, { useState, useCallback, useEffect } from 'react';
import { ConciseRequestDetailsView } from './ViewRequest/ConciseRequestDetailsView';
import './EnterpriseTable.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// Define ViewType to match App.tsx
type ViewType = 'dashboard' | 'create' | 'view' | 'pending' | 'reports' | 'admin' | 'appadmin' | 'useradmin';

// Region interface for region data
interface Region {
  regionId: number;
  regionName: string;
  regionCode: string;
  description?: string;
  isActive: boolean;
}

// Workflow information interface
interface WorkflowInfo {
  workflowId: number;
  currentState: string;
  stateChangedAt: string;
  stateChangedBy: string;
  pendingTasks: number;
  completedTasks: number;
  rejectedTasks: number;
  cancelledTasks: number;
  totalTasks: number;
  workflowProgress: number;
}

interface GatePassRecord {
  id: number;
  name: string;
  email: string;
  companyName: string;
  idDetails: string;
  consignmentDetails: string;
  gateNumbers?: string;
  gateCount?: number;
  personnelList?: string;
  personnelCount?: number;
  detailedDescription?: string;
  approverEmail: string;
  approverUserId?: string;
  reviewerEmail?: string;
  reviewerUserId?: string;
  selectedFunctionId?: number;
  selectedCompanyId?: number;
  status: string;
  submittedAt: string;
  submittedBy: string;
  requesterUserId: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  fileType?: string;
  uploadedAt?: string;
  workflow?: WorkflowInfo | null;
  region?: string;
  regionId?: number;
  requestType?: 'new' | 'supplementary';
  passType?: 'single_visit' | 'short_term' | 'long_term';
  contractNumber?: string;
  startDate?: string;
  endDate?: string;
  passIdentificationName?: string;
  parentRequestId?: number | null;
}

interface DetailedGatePassRecord extends GatePassRecord {
  gates?: Array<{
    gateId: number;
    gateNumber: string;
    locationId: number;
    locationName: string;
    isActive: boolean;
    description?: string;
  }>;
  personnel?: Array<{
    gatePassPersonnelId: number;
    personnelId: number;
    nationalIdNo: string;
    dateOfBirth: string;
    nationality: string;
    typeOfVisa?: string;
    visaExpiry?: string;
    bloodGroup?: string;
    photoFileName?: string;
    photoFileUrl?: string;
    photoFileBlobName?: string;
    photoFileSize?: number;
    photoFileType?: string;
    documentFileName?: string;
    documentFileUrl?: string;
    documentFileBlobName?: string;
    documentFileSize?: number;
    documentFileType?: string;
    companyId: number;
    companyName?: string;
    createdAt: string;
  }>;
}

interface StatusMessage {
  type: 'success' | 'error' | null;
  message: string;
}

interface RequestsViewProps {
  applications: GatePassRecord[];
  isLoadingApplications: boolean;
  onNavigation: (view: ViewType) => void;
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: StatusMessage) => void;
  viewType?: 'my' | 'pending';
}

export const RequestsView: React.FC<RequestsViewProps> = ({
  applications,
  isLoadingApplications,
  onNavigation,
  getAccessToken,
  onStatusUpdate,
  viewType = 'my'
}) => {
  // View mode state - 'list' shows all requests, 'detail' shows single request
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedRequestForDetail, setSelectedRequestForDetail] = useState<GatePassRecord | null>(null);
  
  const [detailedData, setDetailedData] = useState<{ [key: number]: DetailedGatePassRecord }>({});
  const [loadingDetails, setLoadingDetails] = useState<{ [key: number]: boolean }>({});
  const [detailErrors, setDetailErrors] = useState<{ [key: number]: string }>({});
  
  // Region state management
  const [regions, setRegions] = useState<Region[]>([]);

  // Filtering and sorting
  const [sortBy, setSortBy] = useState<'submittedAt' | 'status' | 'companyName' | 'startDate'>('submittedAt');
  const [filterBy, setFilterBy] = useState<string>('all');

  // Fetch regions for display purposes
  const fetchRegions = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/regions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setRegions(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([
        { regionId: 1, regionName: 'North America', regionCode: 'NA', isActive: true },
        { regionId: 2, regionName: 'Europe', regionCode: 'EU', isActive: true },
        { regionId: 3, regionName: 'Asia Pacific', regionCode: 'APAC', isActive: true },
        { regionId: 4, regionName: 'Latin America', regionCode: 'LATAM', isActive: true },
        { regionId: 5, regionName: 'Middle East & Africa', regionCode: 'MEA', isActive: true }
      ]);
    }
  }, [getAccessToken]);

  // Load regions on component mount
  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // Helper function to get region display name


  // Workflow state display mapping — neutral palette
  const getWorkflowStateDisplay = (state: string): { label: string; dotColor: string } => {
    const stateMap: { [key: string]: { label: string; dotColor: string } } = {
      'DRAFT':                    { label: 'Draft',               dotColor: '#9ca3af' },
      'SUBMITTED':                { label: 'Submitted',           dotColor: '#3b82f6' },
      'COMPANY_APPROVER_PENDING': { label: 'Company Approval',    dotColor: '#f59e0b' },
      'COMPANY_REVIEWER_PENDING': { label: 'Company Review',      dotColor: '#f59e0b' },
      'GATE_APPROVER_PENDING':    { label: 'Gate Approval',       dotColor: '#f59e0b' },
      'GATE_REVIEWER_PENDING':    { label: 'Gate Review',         dotColor: '#f59e0b' },
      'PARTIALLY_APPROVED':       { label: 'Partially Approved',  dotColor: '#10b981' },
      'FULLY_APPROVED':           { label: 'Accepted',            dotColor: '#10b981' },
      'REJECTED':                 { label: 'Rejected',            dotColor: '#ef4444' },
      'RETURNED':                 { label: 'Returned',            dotColor: '#f59e0b' },
      'CANCELLED':                { label: 'Cancelled',           dotColor: '#9ca3af' },
      'EXPIRED':                  { label: 'Expired',             dotColor: '#9ca3af' }
    };
    return stateMap[state] || { label: state, dotColor: '#9ca3af' };
  };

  // Get the effective workflow state for a request (prefer workflow.currentState, fallback to status)
  const getEffectiveState = (request: GatePassRecord): string => {
    return request.workflow?.currentState?.toUpperCase() || request.status?.toUpperCase() || '';
  };

  // Map a workflow state to a filter category
  const getFilterCategory = (state: string): string => {
    const upper = state.toUpperCase();
    switch (upper) {
      case 'DRAFT':
        return 'draft';
      case 'SUBMITTED':
      case 'COMPANY_APPROVER_PENDING':
      case 'COMPANY_REVIEWER_PENDING':
      case 'GATE_APPROVER_PENDING':
      case 'GATE_REVIEWER_PENDING':
      case 'PENDING':
        return 'pending';
      case 'RETURNED':
        return 'returned';
      case 'FULLY_APPROVED':
      case 'PARTIALLY_APPROVED':
      case 'APPROVED':
        return 'approved';
      case 'REJECTED':
        return 'rejected';
      case 'CANCELLED':
        return 'cancelled';
      case 'EXPIRED':
        return 'expired';
      default:
        // Any other _PENDING state should map to pending
        if (upper.includes('PENDING')) return 'pending';
        return 'other';
    }
  };

  // Render workflow status — dot + text, no background
  const renderWorkflowStatus = (workflow: WorkflowInfo | null) => {
    if (!workflow) return null;

    const stateInfo = getWorkflowStateDisplay(workflow.currentState);
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#242424'
      }}>
        <span style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          backgroundColor: stateInfo.dotColor,
          flexShrink: 0
        }} />
        {stateInfo.label}
      </span>
    );
  };

  // Handle showing request details in concise view
  const handleShowRequestDetail = (request: GatePassRecord) => {
    setSelectedRequestForDetail(request);
    setViewMode('detail');
    loadRequestDetails(request.id);
  };

  // Handle returning to request list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedRequestForDetail(null);
  };

  // Load detailed information for a gate pass request
  const loadRequestDetails = useCallback(async (requestId: number) => {
    console.log('📋 Loading request details for ID:', requestId);
    
    setDetailErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[requestId];
      return newErrors;
    });
    
    if (detailedData[requestId]) {
      console.log('✅ Details already loaded for request:', requestId);
      return;
    }

    setLoadingDetails(prev => ({ ...prev, [requestId]: true }));

    try {
      const accessToken = await getAccessToken();
      const url = `${API_BASE_URL}/gatepass/${requestId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setDetailedData(prev => ({
            ...prev,
            [requestId]: result.data
          }));
        } else {
          const errorMsg = result.message || 'Failed to load request details';
          setDetailErrors(prev => ({ ...prev, [requestId]: errorMsg }));
        }
      } else {
        const errorText = await response.text();
        
        let userMessage = `Failed to load details (${response.status})`;
        if (response.status === 404) {
          userMessage = 'Request not found or access denied';
        } else if (response.status === 403) {
          userMessage = 'Access denied to this request';
        } else if (response.status === 401) {
          userMessage = 'Authentication failed - please sign in again';
        } else {
          userMessage = `Server error (${response.status}): ${errorText}`;
        }
        
        setDetailErrors(prev => ({ ...prev, [requestId]: userMessage }));
      }
    } catch (error) {
      console.error('💥 Error loading request details:', error);
      
      let errorMessage = 'Error loading request details';
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error - unable to connect to server';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setDetailErrors(prev => ({ ...prev, [requestId]: errorMessage }));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [requestId]: false }));
    }
  }, [getAccessToken, detailedData]);

  // Retry loading details for a specific request
  const retryLoadingDetails = (requestId: number) => {
    setDetailErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[requestId];
      return newErrors;
    });
    
    setDetailedData(prev => {
      const newData = { ...prev };
      delete newData[requestId];
      return newData;
    });
    
    loadRequestDetails(requestId);
  };

  // Download personnel file function
  const downloadPersonnelFile = useCallback(async (requestId: number, personnelId: number, fileType: 'photo' | 'document', fileName: string) => {
    try {
      const accessToken = await getAccessToken();
      const downloadUrl = `${API_BASE_URL}/gatepass/${requestId}/personnel/${personnelId}/${fileType}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let userMessage = `Download failed (${response.status}): ${errorText}`;
        
        if (response.status === 403) {
          userMessage = 'Access denied. You can only download files from your own requests.';
        } else if (response.status === 404) {
          userMessage = `Personnel ${fileType} not found for this request.`;
        }
        
        onStatusUpdate({
          type: 'error',
          message: userMessage
        });
        return;
      }

      const responseText = await response.text();
      let data: any = null;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        onStatusUpdate({
          type: 'error',
          message: 'Invalid response format from server'
        });
        return;
      }
      
      if (data.success && data.downloadUrl) {
        const newWindow = window.open(data.downloadUrl, '_blank');
        
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = data.downloadUrl;
          link.download = data.fileName || fileName;
          link.target = '_blank';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setTimeout(() => {
            if (data?.downloadUrl) {
              window.location.href = data.downloadUrl;
            }
          }, 1000);
        }
        
        onStatusUpdate({
          type: 'success',
          message: `Download initiated for personnel ${fileType}: ${data.fileName || fileName}`
        });
      } else {
        onStatusUpdate({
          type: 'error',
          message: data.error || 'Invalid response from download endpoint'
        });
      }
    } catch (error) {
      let errorMessage = 'Failed to download personnel file';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error - unable to connect to server';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      onStatusUpdate({
        type: 'error',
        message: `Personnel file download error: ${errorMessage}`
      });
    }
  }, [getAccessToken, onStatusUpdate]);

  const downloadFile = useCallback(async (requestId: number, fileName: string) => {
    try {
      const accessToken = await getAccessToken();
      const downloadUrl = `${API_BASE_URL}/gatepass/${requestId}/file`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        let userMessage = `Download failed (${response.status}): ${errorText}`;
        
        if (response.status === 403) {
          userMessage = 'Access denied. You can only download files from your own requests.';
        } else if (response.status === 404) {
          userMessage = 'File not found for this request.';
        }
        
        onStatusUpdate({
          type: 'error',
          message: userMessage
        });
        return;
      }

      const responseText = await response.text();
      let data: any = null;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        onStatusUpdate({
          type: 'error',
          message: 'Invalid response format from server'
        });
        return;
      }
      
      if (data.success && data.downloadUrl) {
        const newWindow = window.open(data.downloadUrl, '_blank');
        
        if (!newWindow) {
          const link = document.createElement('a');
          link.href = data.downloadUrl;
          link.download = data.fileName || fileName;
          link.target = '_blank';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setTimeout(() => {
            if (data?.downloadUrl) {
              window.location.href = data.downloadUrl;
            }
          }, 1000);
        }
        
        onStatusUpdate({
          type: 'success',
          message: `Download initiated for: ${data.fileName || fileName}`
        });
      } else {
        onStatusUpdate({
          type: 'error',
          message: data.error || 'Invalid response from download endpoint'
        });
      }
    } catch (error) {
      let errorMessage = 'Failed to download file';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error - unable to connect to server';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      onStatusUpdate({
        type: 'error',
        message: `Download error: ${errorMessage}`
      });
    }
  }, [getAccessToken, onStatusUpdate]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format relative time
  const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get status badge styling — neutral dot style
  const getStatusBadgeStyle = (status: string) => {
    const dotColors: { [key: string]: string } = {
      'SUBMITTED': '#3b82f6',
      'PENDING': '#f59e0b',
      'APPROVED': '#10b981',
      'REJECTED': '#ef4444',
      'CANCELLED': '#9ca3af',
      'EXPIRED': '#9ca3af'
    };
    
    return {
      dotColor: dotColors[status.toUpperCase()] || '#9ca3af',
      label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    };
  };

  // Filter and sort logic
  const filteredRequests = applications.filter(request => {
    if (filterBy === 'all') return true;
    const effectiveState = getEffectiveState(request);
    return getFilterCategory(effectiveState) === filterBy;
  });

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    switch (sortBy) {
      case 'submittedAt':
        return new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime();
      case 'status':
        return a.status.localeCompare(b.status);
      case 'companyName':
        return a.companyName.localeCompare(b.companyName);
      case 'startDate':
        const aStartDate = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bStartDate = b.startDate ? new Date(b.startDate).getTime() : 0;
        return bStartDate - aStartDate;
      default:
        return 0;
    }
  });

  // Determine which view to render
  const renderContent = () => {

  // Loading state
  if (isLoadingApplications) {
    return (
      <div className="company-management">
        <div className="companies-container">
          <div className="loading">
            <span className="spinner"></span>
            Loading requests...
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (applications.length === 0) {
    return (
      <div className="company-management">
        <div className="companies-container">
          <div className="no-companies">
            <div className="no-data-icon">📋</div>
            <h3>{viewType === 'pending' ? 'No Pending Requests' : 'No Gate Pass Requests'}</h3>
            <p>
              {viewType === 'pending' 
                ? 'No requests are currently pending your approval.' 
                : "You haven't submitted any gate pass requests yet."
              }
            </p>
            {viewType !== 'pending' && (
              <button 
                onClick={() => onNavigation('create')}
                className="create-first-btn"
              >
                Create Your First Request
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CONCISE DETAIL VIEW - Show when details are loaded
  if (viewMode === 'detail' && selectedRequestForDetail) {
    const request = selectedRequestForDetail;
    const details = detailedData[request.id];
    const isLoadingDetail = loadingDetails[request.id];
    const detailError = detailErrors[request.id];

    // Show concise view when data is ready
    if (details && !isLoadingDetail && !detailError) {
      return (
        <ConciseRequestDetailsView
          requestData={{
            id: details.id,
            name: details.name,
            email: details.email,
            companyName: details.companyName,
            regionId: details.regionId,
            requestType: details.requestType,
            passType: details.passType,
            contractNumber: details.contractNumber,
            startDate: details.startDate,
            endDate: details.endDate,
            passIdentificationName: details.passIdentificationName,
            parentRequestId: details.parentRequestId,
            consignmentDetails: details.consignmentDetails,
            detailedDescription: details.detailedDescription,
            gates: details.gates,
            personnel: details.personnel,
            fileName: details.fileName,
            fileSize: details.fileSize,
            fileType: details.fileType,
            uploadedAt: details.uploadedAt,
            approverEmail: details.approverEmail,
            reviewerEmail: details.reviewerEmail,
            workflow: details.workflow
          }}
          regions={regions}
          onClose={handleBackToList}
          formatFileSize={formatFileSize}
          onDownloadFile={downloadFile}
          onDownloadPersonnelFile={downloadPersonnelFile}
          getAccessToken={getAccessToken}
          onStatusUpdate={onStatusUpdate}
          onRefresh={() => loadRequestDetails(details.id)}
        />
      );
    }

    // Show loading state
    if (isLoadingDetail) {
      return (
        <div className="company-management">
          <div className="breadcrumb-section">
            <button onClick={handleBackToList} className="breadcrumb-link">
              &larr; Back to Requests
            </button>
          </div>
          <div className="companies-container">
            <div className="loading">
              <span className="spinner"></span>
              Loading request details...
            </div>
          </div>
        </div>
      );
    }

    // Show error state
    if (detailError) {
      return (
        <div className="company-management">
          <div className="breadcrumb-section">
            <button onClick={handleBackToList} className="breadcrumb-link">
              &larr; Back to Requests
            </button>
          </div>
          <div className="companies-container" style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              padding: '16px',
              borderRadius: '6px',
              border: '1px solid #fecaca',
              display: 'inline-block',
              textAlign: 'left',
              maxWidth: '500px'
            }}>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>Failed to Load Details</h5>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px' }}>{detailError}</p>
              <button 
                onClick={() => retryLoadingDetails(request.id)}
                className="create-company-btn"
                style={{ fontSize: '12px' }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ─── LIST VIEW ─────────────────────────────────────────
  return (
    <div className="company-management" style={{ background: '#ffffff' }}>
      <div className="companies-container">
        {/* Toolbar */}
        <div className="table-toolbar">
          <div className="toolbar-left">
            <span className="record-count">
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
              {viewType === 'pending' ? ' pending review' : ''}
            </span>
          </div>
          <div className="toolbar-right" style={{ gap: '10px' }}>
            {/* Filter */}
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              style={{
                height: '34px',
                padding: '0 28px 0 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                fontSize: '13px',
                color: '#242424',
                cursor: 'pointer',
                outline: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center'
              }}
            >
              <option value="all">All ({applications.length})</option>
              <option value="pending">Pending ({applications.filter(r => getFilterCategory(getEffectiveState(r)) === 'pending').length})</option>
              <option value="approved">Accepted ({applications.filter(r => getFilterCategory(getEffectiveState(r)) === 'approved').length})</option>
              <option value="rejected">Rejected ({applications.filter(r => getFilterCategory(getEffectiveState(r)) === 'rejected').length})</option>
              <option value="returned">Returned ({applications.filter(r => getFilterCategory(getEffectiveState(r)) === 'returned').length})</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{
                height: '34px',
                padding: '0 28px 0 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                fontSize: '13px',
                color: '#242424',
                cursor: 'pointer',
                outline: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center'
              }}
            >
              <option value="submittedAt">Date ↓</option>
              <option value="status">Status</option>
              <option value="companyName">Company</option>
              <option value="startDate">Start Date</option>
            </select>

            {viewType !== 'pending' && (
              <button 
                onClick={() => onNavigation('create')}
                className="create-company-btn"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>New Request</span>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="companies-table-container">
          <table className="minimalist-table">
            <thead>
              <tr>
                <th style={{ width: '70px' }}>REQ #</th>
                <th>COMPANY</th>
                <th style={{ width: '110px' }}>START DATE</th>
                <th style={{ width: '170px' }}>STATUS</th>
                <th style={{ width: '200px' }}>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {sortedRequests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-results">
                    <div className="no-results-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <p>No requests match the selected filter</p>
                      <button onClick={() => setFilterBy('all')} className="clear-search-link">Show all requests</button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedRequests.map((request) => {
                  const fallbackStatus = getStatusBadgeStyle(request.status);
                  return (
                    <tr 
                      key={request.id} 
                      onClick={() => handleShowRequestDetail(request)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span style={{ color: '#1d4ed8', fontWeight: 600, fontSize: '13px' }}>
                          {request.id}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: '#242424' }}>{request.companyName}</span>
                      </td>
                      <td>
                        <span style={{ color: '#242424' }}>
                          {request.startDate ? formatDate(request.startDate) : '—'}
                        </span>
                      </td>
                      <td>
                        {request.workflow ? renderWorkflowStatus(request.workflow) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: '#242424' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: fallbackStatus.dotColor, flexShrink: 0 }} />
                            {fallbackStatus.label}
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ color: '#6b7280', fontSize: '13px' }}>
                          {request.gateCount || 0} gates · {request.personnelCount || 0} personnel · {formatRelativeTime(request.submittedAt)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="table-footer">
          <div className="table-stats">
            <span className="stat-item">
              Showing <strong>{filteredRequests.length}</strong> of <strong>{applications.length}</strong> requests
            </span>
          </div>
        </div>
      </div>
    </div>
  );
  };

  return (
    <>
      <h1 style={{
        fontSize: '25px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: '0 0 12px 0',
        padding: '5px 0 0 20px',
        lineHeight: 1.3,
        fontFamily: "'Segoe UI', sans-serif",
        letterSpacing: '-0.01em',
      }}>
        {viewType === 'pending' ? 'Requests for My Approval' : 'My Gate Pass Requests'}
      </h1>

      {renderContent()}
    </>
  );
};